import * as React from "react";

import { Text } from "@uikit";
import MailingListSignup from "../mailingListSignup";
import { Link, graphql, useStaticQuery } from "gatsby";
import { CourseExtended } from "@kalabo/golfsavers-common";
import { ChevronDownIcon } from "@heroicons/react/outline";
import { useApp } from "@store/app";
import { slugify } from "@kalabo/golfsavers-common";
import {
  getMailingListTags,
  isRegionalSite,
  useRootCourseUrls,
} from "@utilities/core";

interface iProps {
  focusedMode: boolean;
}

const Footer = ({ focusedMode = false }: iProps) => {
  const display_currency = useApp(($) => $.display_currency);
  const setShowCurrencyPicker = useApp(($) => $.setShowCurrencyPicker);
  const [coursesExpanded, setCoursesExpanded] = React.useState(false);
  const [countriesExpanded, setCountriesExpanded] = React.useState(false);
  const [popularExpanded, setPopularExpanded] = React.useState(false);
  const data = useStaticQuery(graphql`
    query {
      countries: allGolfsaversCountries {
        edges {
          node {
            id
            slug
            name {
              en
            }
          }
        }
      }
      regions: allGolfsaversRegions {
        edges {
          node {
            id
            name {
              en
            }
            slug
            top_courses
            region_id
          }
        }
      }
      courses: allGolfsaversGolfCourses(filter: { published: { eq: true } }) {
        edges {
          node {
            id
            images {
              alt
              url
            }
            course_id
            slug
            name {
              en
            }
            region {
              country {
                slug
              }
              name {
                en
              }
              slug
            }
          }
        }
      }
    }
  `);

  let current_region = data.regions.edges?.find(
    (region: any) => region.node.region_id === process.env.GATSBY_REGION_ID
  )?.node;

  let countries = data.countries.edges.map((edge: any) => edge.node);

  const navigation = React.useMemo(() => {
    let links: any = {
      footer: [
        {
          name: "Privacy",
          href: "/privacy-policy",
        },
        {
          name: "Terms",
          href: "/terms-and-conditions",
        },
      ],
      quicklinks: [
        { name: "Home", href: "/" },
        { name: "About Us", href: "/about-us" },
        { name: "Our Reviews", href: "/reviews" },
        { name: "Manage Your Booking", href: "/account" },
      ],
      social: [
        {
          name: "Facebook",
          href: "https://www.facebook.com/golfsavers",
          icon: (props: any) => (
            <svg fill="currentColor" viewBox="0 0 24 24" {...props}>
              <path
                d="M20.25 0H3.75C1.67893 0 0 1.67893 0 3.75V20.25C0 22.3211 1.67893 24 3.75 24H20.25C22.3211 24 24 22.3211 24 20.25V3.75C24 1.67893 22.3211 0 20.25 0Z"
                fill="#1877F2"
              />
              <path
                d="M18 12C18 8.7 15.3 6 12 6C8.7 6 6 8.7 6 12C6 15 8.175 17.475 11.025 17.925V13.725H9.525V12H11.025V10.65C11.025 9.15 11.925 8.325 13.275 8.325C13.95 8.325 14.625 8.475 14.625 8.475V9.975H13.875C13.125 9.975 12.9 10.425 12.9 10.875V12H14.55L14.25 13.725H12.825V18C15.825 17.55 18 15 18 12Z"
                fill="white"
              />
            </svg>
          ),
        },
        {
          name: "Twitter",
          href: "https://twitter.com/GolfSavers",
          icon: (props: any) => (
            <svg fill="currentColor" viewBox="0 0 24 24" {...props}>
              <path
                d="M20.25 0H3.75C1.67893 0 0 1.67893 0 3.75V20.25C0 22.3211 1.67893 24 3.75 24H20.25C22.3211 24 24 22.3211 24 20.25V3.75C24 1.67893 22.3211 0 20.25 0Z"
                fill="#1DA1F2"
              />
              <path
                d="M18 8.25C17.55 8.475 17.1 8.55 16.575 8.625C17.1 8.325 17.475 7.875 17.625 7.275C17.175 7.575 16.65 7.725 16.05 7.875C15.6 7.425 14.925 7.125 14.25 7.125C12.675 7.125 11.475 8.625 11.85 10.125C9.825 10.05 8.025 9.075 6.75 7.575C6.075 8.7 6.45 10.125 7.5 10.875C7.125 10.875 6.75 10.725 6.375 10.575C6.375 11.7 7.2 12.75 8.325 13.05C7.95 13.125 7.575 13.2 7.2 13.125C7.5 14.1 8.4 14.85 9.525 14.85C8.625 15.525 7.275 15.9 6 15.75C7.125 16.425 8.4 16.875 9.75 16.875C14.325 16.875 16.875 13.05 16.725 9.525C17.25 9.225 17.7 8.775 18 8.25Z"
                fill="white"
              />
            </svg>
          ),
        },
        {
          name: "LinkedIn",
          href: "http://www.linkedin.com/company/golfsavers-pte-ltd",
          icon: (props: any) => (
            <svg fill="currentColor" viewBox="0 0 24 24" {...props}>
              <path
                d="M20.25 0H3.75C1.67893 0 0 1.67893 0 3.75V20.25C0 22.3211 1.67893 24 3.75 24H20.25C22.3211 24 24 22.3211 24 20.25V3.75C24 1.67893 22.3211 0 20.25 0Z"
                fill="#2867B2"
              />
              <path
                d="M8.7 18H6.15V9.975H8.7V18ZM7.425 8.85C6.6 8.85 6 8.25 6 7.425C6 6.6 6.675 6 7.425 6C8.25 6 8.85 6.6 8.85 7.425C8.85 8.25 8.25 8.85 7.425 8.85ZM18 18H15.45V13.65C15.45 12.375 14.925 12 14.175 12C13.425 12 12.675 12.6 12.675 13.725V18H10.125V9.975H12.525V11.1C12.75 10.575 13.65 9.75 14.925 9.75C16.35 9.75 17.85 10.575 17.85 13.05V18H18Z"
                fill="white"
              />
            </svg>
          ),
        },
        {
          name: "YouTube",
          href: "https://www.youtube.com/channel/UCHkOLrhteTB75jyxTd_Z6jg",
          icon: (props: any) => (
            <svg fill="currentColor" viewBox="0 0 24 24" {...props}>
              <path
                d="M20.25 0H3.75C1.67893 0 0 1.67893 0 3.75V20.25C0 22.3211 1.67893 24 3.75 24H20.25C22.3211 24 24 22.3211 24 20.25V3.75C24 1.67893 22.3211 0 20.25 0Z"
                fill="#FF0000"
              />
              <path
                d="M17.7 9.07578C17.55 8.55078 17.175 8.17578 16.65 8.02578C15.75 7.80078 11.925 7.80078 11.925 7.80078C11.925 7.80078 8.175 7.80078 7.2 8.02578C6.675 8.17578 6.3 8.55078 6.15 9.07578C6 10.0508 6 12.0008 6 12.0008C6 12.0008 6 13.9508 6.225 14.9258C6.375 15.4508 6.75 15.8258 7.275 15.9758C8.175 16.2008 12 16.2008 12 16.2008C12 16.2008 15.75 16.2008 16.725 15.9758C17.25 15.8258 17.625 15.4508 17.775 14.9258C18 13.9508 18 12.0008 18 12.0008C18 12.0008 18 10.0508 17.7 9.07578ZM10.8 13.8008V10.2008L13.95 12.0008L10.8 13.8008Z"
                fill="white"
              />
            </svg>
          ),
        },
        {
          name: "Instagram",
          href: "https://instagram.com/golfsavers/",
          icon: (props: any) => (
            <svg fill="currentColor" viewBox="0 0 24 24" {...props}>
              <path
                d="M20.25 0H3.75C1.67893 0 0 1.67893 0 3.75V20.25C0 22.3211 1.67893 24 3.75 24H20.25C22.3211 24 24 22.3211 24 20.25V3.75C24 1.67893 22.3211 0 20.25 0Z"
                fill="#F00073"
              />
              <path
                d="M11.9999 6.90039C13.6499 6.90039 13.8749 6.90039 14.5499 6.90039C15.1499 6.90039 15.4499 7.05039 15.6749 7.12539C15.9749 7.27539 16.1999 7.35039 16.4249 7.57539C16.6499 7.80039 16.7999 8.02539 16.8749 8.32539C16.9499 8.55039 17.0249 8.85039 17.0999 9.45039C17.0999 10.1254 17.0999 10.2754 17.0999 12.0004C17.0999 13.7254 17.0999 13.8754 17.0999 14.5504C17.0999 15.1504 16.9499 15.4504 16.8749 15.6754C16.7249 15.9754 16.6499 16.2004 16.4249 16.4254C16.1999 16.6504 15.9749 16.8004 15.6749 16.8754C15.4499 16.9504 15.1499 17.0254 14.5499 17.1004C13.8749 17.1004 13.7249 17.1004 11.9999 17.1004C10.2749 17.1004 10.1249 17.1004 9.4499 17.1004C8.8499 17.1004 8.5499 16.9504 8.3249 16.8754C8.0249 16.7254 7.7999 16.6504 7.5749 16.4254C7.3499 16.2004 7.1999 15.9754 7.1249 15.6754C7.0499 15.4504 6.9749 15.1504 6.8999 14.5504C6.8999 13.8754 6.8999 13.7254 6.8999 12.0004C6.8999 10.2754 6.8999 10.1254 6.8999 9.45039C6.8999 8.85039 7.0499 8.55039 7.1249 8.32539C7.2749 8.02539 7.3499 7.80039 7.5749 7.57539C7.7999 7.35039 8.0249 7.20039 8.3249 7.12539C8.5499 7.05039 8.8499 6.97539 9.4499 6.90039C10.1249 6.90039 10.3499 6.90039 11.9999 6.90039ZM11.9999 5.77539C10.2749 5.77539 10.1249 5.77539 9.4499 5.77539C8.7749 5.77539 8.3249 5.92539 7.9499 6.07539C7.5749 6.22539 7.1999 6.45039 6.8249 6.82539C6.4499 7.20039 6.2999 7.50039 6.0749 7.95039C5.9249 8.32539 5.8499 8.77539 5.7749 9.45039C5.7749 10.1254 5.7749 10.3504 5.7749 12.0004C5.7749 13.7254 5.7749 13.8754 5.7749 14.5504C5.7749 15.2254 5.9249 15.6754 6.0749 16.0504C6.2249 16.4254 6.4499 16.8004 6.8249 17.1754C7.1999 17.5504 7.4999 17.7004 7.9499 17.9254C8.3249 18.0754 8.7749 18.1504 9.4499 18.2254C10.1249 18.2254 10.3499 18.2254 11.9999 18.2254C13.6499 18.2254 13.8749 18.2254 14.5499 18.2254C15.2249 18.2254 15.6749 18.0754 16.0499 17.9254C16.4249 17.7754 16.7999 17.5504 17.1749 17.1754C17.5499 16.8004 17.6999 16.5004 17.9249 16.0504C18.0749 15.6754 18.1499 15.2254 18.2249 14.5504C18.2249 13.8754 18.2249 13.6504 18.2249 12.0004C18.2249 10.3504 18.2249 10.1254 18.2249 9.45039C18.2249 8.77539 18.0749 8.32539 17.9249 7.95039C17.7749 7.57539 17.5499 7.20039 17.1749 6.82539C16.7999 6.45039 16.4999 6.30039 16.0499 6.07539C15.6749 5.92539 15.2249 5.85039 14.5499 5.77539C13.8749 5.77539 13.7249 5.77539 11.9999 5.77539Z"
                fill="white"
              />
              <path
                d="M11.9999 8.77539C10.1999 8.77539 8.7749 10.2004 8.7749 12.0004C8.7749 13.8004 10.1999 15.2254 11.9999 15.2254C13.7999 15.2254 15.2249 13.8004 15.2249 12.0004C15.2249 10.2004 13.7999 8.77539 11.9999 8.77539ZM11.9999 14.1004C10.8749 14.1004 9.8999 13.2004 9.8999 12.0004C9.8999 10.8754 10.7999 9.90039 11.9999 9.90039C13.1249 9.90039 14.0999 10.8004 14.0999 12.0004C14.0999 13.1254 13.1249 14.1004 11.9999 14.1004Z"
                fill="white"
              />
              <path
                d="M15.2999 9.45039C15.7141 9.45039 16.0499 9.1146 16.0499 8.70039C16.0499 8.28618 15.7141 7.95039 15.2999 7.95039C14.8857 7.95039 14.5499 8.28618 14.5499 8.70039C14.5499 9.1146 14.8857 9.45039 15.2999 9.45039Z"
                fill="white"
              />
            </svg>
          ),
        },
      ],
    };

    if (isRegionalSite()) {
      let golf = {
        name: `${current_region?.name?.en} Golf Courses`,
        href: `/${current_region?.slug}-golf-courses/`,
      };

      let greenfees = {
        name: `${current_region?.name?.en} Green Fees`,
        href: `/${current_region?.slug}-green-fees/`,
      };

      let hotel = {
        name: `${current_region?.name?.en} Golf Packages`,
        href: `/${current_region?.slug}-golf-packages/`,
      };

      // add these two after the first item in navigation.quicklinks
      links.quicklinks.splice(1, 0, golf);
      links.quicklinks.splice(2, 0, greenfees);
      links.quicklinks.splice(3, 0, hotel);
    } else {
      let blog = { name: "Blog", href: "/blog" };
      links.quicklinks.push(blog);
    }

    return links;
  }, [current_region]);

  const top_courses = React.useMemo(() => {
    let links: any[] = [];
    let results = data.courses.edges.map((a: any) => {
      let images = a.node.images;
      let featured_image = images?.length > 0 ? images[0].url : null;

      return {
        ...a.node,
        image: featured_image,
      };
    });

    if (current_region && isRegionalSite()) {
      results.sort((a: any, b: any) => {
        return (
          current_region?.top_courses.indexOf(a.course_id) -
          current_region?.top_courses.indexOf(b.course_id)
        );
      });
    }

    if (isRegionalSite()) {
      links.push({
        id: "tee_times",
        name: `Tee Times in ${process.env.GATSBY_REGION}`,
        href: `/${slugify(
          process.env.GATSBY_REGION.toLowerCase()
        )}-golf-courses`,
      });

      links.push({
        id: "golf_holidays",
        name: `Golf Holidays in ${process.env.GATSBY_REGION}`,
        href: `/${slugify(
          process.env.GATSBY_REGION.toLowerCase()
        )}-golf-packages`,
      });
    }

    results.forEach((course: any) => {
      links.push({
        id: `${course.id}_tee_times`,
        name: `${course.name.en} Tee Times`,
        image: course.image,
        href: `${isRegionalSite() ? "" : `/${course.region.country.slug}`}/${
          useRootCourseUrls()
            ? `${course.slug}`
            : `${slugify(course.region.name.en.toLowerCase())}-golf-courses/${
                course.slug
              }`
        }`,
      });
      links.push({
        id: `${course.id}_green_fees`,
        name: `${course.name.en} Green Fees`,
        href: `${isRegionalSite() ? "" : `/${course.region.country.slug}`}/${
          useRootCourseUrls()
            ? `${course.slug}`
            : `${slugify(course.region.name.en.toLowerCase())}-golf-courses/${
                course.slug
              }`
        }`,
      });
    });

    return links;
  }, [current_region, data.courses.edges]);

  const courses = data.courses.edges
    .map((edge: any) => edge.node as CourseExtended)
    .sort((a: any, b: any) => a.name.en.localeCompare(b.name.en));

  if (focusedMode) {
    return (
      <div className="border-t bg-grey border-bordergrey2 ">
        <div className="hidden lg:max-w-[1000px] xl:max-w-[1120px] mx-auto  px-[20px] md:px-[40px] lg:px-[0px] md:flex sticky bottom-0 inset-x-0  py-4  flex-wrap justify-between space-x-4">
          <Text size={11}>
            &copy; {new Date().getFullYear()} Golfsavers Pte. Ltd.
          </Text>
          <div className="flex gap-2">
            {navigation.footer.map((item, idx) => {
              return (
                <Link
                  to={item.href}
                  key={idx}
                  aria-label={`Go to homepage ${item.name}`}
                >
                  <Text size={11}>{item.name}</Text>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <MailingListSignup tags={getMailingListTags()} />
      <footer id="footer" className="bg-white">
        <Text size={11} className="sr-only">
          Footer
        </Text>
        <div className="lg:max-w-[1000px] xl:max-w-[1120px] mx-auto px-[20px] md:px-10 lg:px-0 py-12 ">
          <div className="grid grid-cols-12 md:gap-12">
            <div className="col-span-12 md:col-span-4 text-left flex flex-col gap-10 justify-between">
              <div className="flex flex-1 justify-start flex-col text-left">
                <div className="flex">
                  <img
                    height={35}
                    width={"100%"}
                    loading="lazy"
                    alt={process.env.GATSBY_SITE_URL}
                    src={process.env.GATSBY_LOGO}
                    className="max-h-[35px] w-auto mb-5 "
                  />
                </div>
                <div className="mb-6">
                  <Text
                    size={13}
                    weight="semibold"
                    leading={1.3}
                    className="mb-2"
                  >
                    Trusted · Effortless · Competitive
                  </Text>
                  <Text size={13} leading={1.5} color="black80">
                    As part of the Golfsavers group,{" "}
                    {process.env.GATSBY_SITE_URL} is the trusted choice for
                    discounted tee times & golf packages in{" "}
                    {process.env.GATSBY_REGION}. Since 2014, we have organised
                    tee times and golf holidays for over 100,000 golfers.
                  </Text>
                </div>
                <div className="flex space-x-4">
                  {navigation.social.map((item, iIdx) => (
                    <a
                      key={iIdx}
                      href={item.href}
                      target="_blank"
                      aria-label={item.name}
                    >
                      <span className="sr-only">{item.name}</span>
                      <item.icon className="h-7 w-7" aria-hidden="true" />
                    </a>
                  ))}
                </div>
              </div>
              <div className="flex-row hidden sm:flex">
                <div className=" border-r border-grey3 pr-4 md:pr-2 lg:pr-4">
                  <img
                    src={`${process.env.GATSBY_IMAGE_PREFIX}/website/10years.png`}
                    alt="10 years in business"
                    className="h-10 md:h-7 lg:h-10"
                  />
                </div>
                <div className="border-r border-grey3 px-4 md:px-2 lg:px-4">
                  <img
                    src={`${process.env.GATSBY_IMAGE_PREFIX}/website/world-golf-awards-nominee.png`}
                    alt="World Golf Awards Nominee"
                    className="h-10 md:h-7 lg:h-10"
                  />
                </div>
                <div className="border-r border-grey3 px-4 md:px-2 lg:px-4">
                  <img
                    src={`${process.env.GATSBY_IMAGE_PREFIX}/website/iagto.png`}
                    alt="The Global Golf Tourism Organisation"
                    className="h-10 md:h-7 lg:h-10"
                  />
                </div>
                <div className="pl-4 md:pl-2 lg:pl-4">
                  <img
                    src={`${process.env.GATSBY_IMAGE_PREFIX}/website/taot.png`}
                    alt="Tourism Authority of Thailand"
                    className="h-10 md:h-7 lg:h-10"
                  />
                </div>
              </div>
            </div>
            <div className="col-span-12 md:col-span-8 mt-12 md:mt-3">
              <div className="grid grid-cols-12 gap-4">
                <div className="col-span-12 md:col-span-4">
                  <Text size={13} weight="semibold">
                    Quick Links
                  </Text>
                  <div className="mt-4 mb-4">
                    <ul role="list" className="mt-4 mb-4  ">
                      {navigation.quicklinks.map((item) => (
                        <li key={item.name}>
                          <Link
                            to={item.href}
                            className="py-1.5 inline-block hover:underline  text-ellipsis overflow-hidden w-full whitespace-nowrap"
                            aria-label={`Go to homepage ${item.name}`}
                          >
                            <Text
                              size={12}
                              color="black60"
                              weight="semibold"
                              leading={1.3}
                              className=" text-ellipsis overflow-hidden w-full whitespace-nowrap"
                            >
                              {item.name}
                            </Text>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                {isRegionalSite() ? (
                  <div className="col-span-12 mb-10 sm:mb-0 md:col-span-4">
                    <Text size={13} weight="semibold">
                      Golf Courses
                    </Text>
                    <ul role="list" className="mt-4 mb-4">
                      {courses
                        .map((item: any, idx: any) => {
                          return (
                            <li key={`${item.name.en}-courses-${idx}`}>
                              <Link
                                className="py-1.5 inline-block hover:underline text-ellipsis overflow-hidden w-full whitespace-nowrap"
                                to={
                                  useRootCourseUrls()
                                    ? `/${item.slug}`
                                    : `/${slugify(
                                        item.region.name.en.toLowerCase()
                                      )}-golf-courses/${item.slug}`
                                }
                                aria-label={`Go to homepage ${item.name.en}`}
                              >
                                <Text
                                  size={12}
                                  color="black60"
                                  weight="semibold"
                                  leading={1.3}
                                  className="text-ellipsis overflow-hidden w-full whitespace-nowrap"
                                >
                                  {item.name.en}
                                </Text>
                              </Link>
                            </li>
                          );
                        })
                        .slice(0, 14)}
                      {courses
                        .map((item: any, idx: any) => {
                          return (
                            <li
                              key={`${item.name.en}-courses-${idx}`}
                              className={`${
                                coursesExpanded ? "" : "hidden"
                              } text-ellipsis overflow-hidden w-full whitespace-nowrap`}
                            >
                              <Link
                                to={
                                  useRootCourseUrls()
                                    ? `/${item.slug}`
                                    : `/${slugify(
                                        item.region.name.en.toLowerCase()
                                      )}-golf-courses/${item.slug}`
                                }
                                className="py-1.5 inline-block hover:underline text-ellipsis overflow-hidden w-full whitespace-nowrap"
                                aria-label={`Go to homepage ${item.name.en}`}
                              >
                                <Text
                                  size={12}
                                  color="black60"
                                  weight="semibold"
                                  leading={1.3}
                                  className="text-ellipsis overflow-hidden w-full whitespace-nowrap"
                                >
                                  {item.name.en}
                                </Text>
                              </Link>
                            </li>
                          );
                        })
                        .slice(14, courses.length)}
                    </ul>
                    {courses.length > 14 && (
                      <div
                        className="cursor-pointer"
                        onClick={() => {
                          setCoursesExpanded(!coursesExpanded);
                        }}
                      >
                        <Text
                          size={11}
                          leading={1}
                          color="blue"
                          weight="semibold"
                        >
                          {!coursesExpanded ? "More" : "Less"}{" "}
                          <ChevronDownIcon
                            className={`h-4 w-4 inline-block ${
                              coursesExpanded ? "rotate-180" : ""
                            }`}
                          />
                        </Text>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="col-span-12 md:col-span-4">
                    <Text size={13} weight="semibold">
                      Countries
                    </Text>

                    <div className="mt-4 mb-4">
                      <ul role="list">
                        {countries
                          .map((item: any) => {
                            return (
                              <li key={`${item.name}-countries`}>
                                <Link
                                  className="py-1.5 inline-block hover:underline text-ellipsis overflow-hidden w-full whitespace-nowrap"
                                  to={`/${item.slug}`}
                                  aria-label={`Go to homepage ${item.name}`}
                                >
                                  <Text
                                    size={12}
                                    color="black60"
                                    weight="semibold"
                                    leading={1.3}
                                    className="text-ellipsis overflow-hidden w-full whitespace-nowrap"
                                  >
                                    {item.name.en}
                                  </Text>
                                </Link>
                              </li>
                            );
                          })
                          .slice(0, 14)}
                        {countries
                          .map((item: any) => {
                            return (
                              <li key={`${item.name}-countries`}>
                                <Link
                                  className={`${
                                    countriesExpanded ? "" : "hidden"
                                  } py-1.5 inline-block hover:underline text-ellipsis overflow-hidden w-full whitespace-nowrap`}
                                  to={`/${item.slug}`}
                                  aria-label={`Go to homepage ${item.name}`}
                                >
                                  <Text
                                    size={12}
                                    color="black60"
                                    weight="semibold"
                                    leading={1.3}
                                    className="text-ellipsis overflow-hidden w-full whitespace-nowrap"
                                  >
                                    {item.name.en}
                                  </Text>
                                </Link>
                              </li>
                            );
                          })
                          .slice(14, countries.length)}
                      </ul>
                      {countries.length > 14 && (
                        <div
                          className="cursor-pointer"
                          onClick={() => {
                            setCountriesExpanded(!countriesExpanded);
                          }}
                        >
                          <Text
                            size={11}
                            leading={1}
                            color="blue"
                            weight="semibold"
                          >
                            {!countriesExpanded ? "More" : "Less"}{" "}
                            <ChevronDownIcon
                              className={`h-4 w-4 inline-block ${
                                countriesExpanded ? "rotate-180" : ""
                              }`}
                            />
                          </Text>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {isRegionalSite() ? (
                  <div className="col-span-12  mb-10 sm:mb-0  md:col-span-4">
                    <Text size={13} weight="semibold">
                      Popular Searches
                    </Text>

                    <ul role="list" className="mt-4 mb-4">
                      {top_courses
                        .map((item: any) => {
                          return (
                            <li key={`${item.name}-popular`}>
                              {item.image && (
                                <img
                                  className="w-[70px] rounded-[4px] hidden"
                                  src={`${process.env.GATSBY_IMAGE_PREFIX}${item.image}?aspect_ratio=1:1&width=70&height=70)`}
                                />
                              )}
                              <Link
                                className="py-1.5 inline-block hover:underline text-ellipsis overflow-hidden w-full whitespace-nowrap"
                                to={`${item.href}`}
                                aria-label={`Go to homepage ${item.name}`}
                              >
                                <Text
                                  size={12}
                                  color="black60"
                                  weight="semibold"
                                  leading={1.3}
                                  className="text-ellipsis overflow-hidden w-full whitespace-nowrap"
                                >
                                  {item.name}
                                </Text>
                              </Link>
                            </li>
                          );
                        })
                        .slice(0, 14)}

                      {top_courses
                        .map((item: any, idx: any) => {
                          return (
                            <li
                              key={`${item.name}-popular-${idx}`}
                              className={`${
                                popularExpanded ? "" : "hidden"
                              } text-ellipsis overflow-hidden w-full whitespace-nowrap`}
                            >
                              <Link
                                className="py-1.5 inline-block hover:underline text-ellipsis overflow-hidden w-full whitespace-nowrap"
                                to={`${item.href}`}
                                aria-label={`Go to homepage ${item.name}`}
                              >
                                <Text
                                  size={12}
                                  color="black60"
                                  weight="semibold"
                                  leading={1.3}
                                  className="text-ellipsis overflow-hidden w-full whitespace-nowrap"
                                >
                                  {item.name}
                                </Text>
                              </Link>
                            </li>
                          );
                        })
                        .slice(14, top_courses.length)}
                    </ul>
                    {top_courses.length > 14 && (
                      <div
                        className="cursor-pointer"
                        onClick={() => {
                          setPopularExpanded(!popularExpanded);
                        }}
                      >
                        <Text
                          size={11}
                          leading={1}
                          color="blue"
                          weight="semibold"
                        >
                          {!popularExpanded ? "More" : "Less"}{" "}
                          <ChevronDownIcon
                            className={`h-4 w-4 inline-block ${
                              popularExpanded ? "rotate-180" : ""
                            }`}
                          />
                        </Text>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="col-span-12 mb-10 sm:mb-0  md:col-span-4">
                    <Text size={13} weight="semibold">
                      Popular Searches
                    </Text>
                    <div className="mt-4 mb-4">
                      <ul role="list">
                        {countries
                          .map((item: any) => {
                            return (
                              <li key={`${item.name}-countries`}>
                                <Link
                                  className="py-1.5 inline-block hover:underline text-ellipsis overflow-hidden w-full whitespace-nowrap"
                                  to={`/${item.slug}`}
                                  aria-label={`Go to homepage ${item.name}`}
                                >
                                  <Text
                                    size={12}
                                    color="black60"
                                    weight="semibold"
                                    leading={1.3}
                                    className="text-ellipsis overflow-hidden w-full whitespace-nowrap"
                                  >
                                    Best Golf Courses in {item.name.en}
                                  </Text>
                                </Link>
                              </li>
                            );
                          })
                          .slice(0, 14)}
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="col-span-12 flex  flex-row sm:hidden mt-10">
              <div className="border-r border-grey3 pr-4">
                <img
                  src={`${process.env.GATSBY_IMAGE_PREFIX}/website/10years.png`}
                  alt="10 years in business"
                  className="h-10 w-auto"
                />
              </div>
              <div className="border-r border-grey3 px-4">
                <img
                  src={`${process.env.GATSBY_IMAGE_PREFIX}/website/world-golf-awards-nominee.png`}
                  alt="World Golf Awards Nominee"
                  className="h-10 w-auto"
                />
              </div>
              <div className="border-r border-grey3 px-4">
                <img
                  src={`${process.env.GATSBY_IMAGE_PREFIX}/website/iagto.png`}
                  alt="The Global Golf Tourism Organisation"
                  className="h-10 w-auto"
                />
              </div>
              <div className="px-4">
                <img
                  src={`${process.env.GATSBY_IMAGE_PREFIX}/website/taot.png`}
                  alt="Tourism Authority of Thailand"
                  className="h-10 w-auto"
                />
              </div>
            </div>
          </div>
          <div className="mt-12 border-t border-bordergrey2 pt-4 flex flex-wrap space-x-4  items-center">
            <div className="flex gap-2 items-center">
              <Text size={11} leading={1} color="black60">
                &copy; {new Date().getFullYear()} Golfsavers Pte. Ltd.
              </Text>
              {navigation.footer.map((item, idx) => {
                return (
                  <React.Fragment key={idx}>
                    <Text
                      size={25}
                      leading={1}
                      color="black60"
                      className="!m-0"
                    >
                      ·
                    </Text>
                    <Link
                      to={item.href}
                      className=" hover:underline"
                      aria-label={`Go to homepage ${item.name}`}
                    >
                      <Text
                        size={11}
                        leading={1}
                        color="black60"
                        className="!m-0"
                      >
                        {item.name}
                      </Text>
                    </Link>
                  </React.Fragment>
                );
              })}
            </div>

            <div
              className="flex flex-1 justify-end"
              onClick={() => {
                setShowCurrencyPicker();
              }}
            >
              <div>
                <Text
                  size={13}
                  leading={1.3}
                  weight="semibold"
                  color="black90"
                  className="cursor-pointer hover:underline hover:!text-blue"
                >
                  {display_currency.symbol} {display_currency.code}
                </Text>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
};
export default Footer;
